import React from 'react';
import { Button, FlexBox, Icon, responsive, tokensRaw, useScreenClass } from '@vp/swan';
import styled from 'styled-components';
import { __ } from '@99designs/i18n';
import { useStepContext } from '../context/StepContext';

const BottomBarContainer = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${tokensRaw.SwanBaseColorWhite};
    border-top: 1px solid ${tokensRaw.SwanBaseColorGrey100};
    box-shadow: 0 -1px 3px ${tokensRaw.SwanBaseColorBlackOpacity10};
    position: fixed;
    left: 0;
    bottom: 0;
    z-index: ${tokensRaw.SwanSemZIndexOverlay};
    width: 100%;
`;

const ResponsiveFlexBox = responsive(FlexBox);
const ResponsiveButton = responsive(Button);

export const BottomBar = ({
    NextAction,
    PreviousAction,
    Information,
    BuyNowAction,
}: {
    NextAction?: React.ComponentType;
    BuyNowAction?: React.ComponentType;
    PreviousAction?: React.ComponentType;
    Information?: React.ComponentType;
}) => {
    const { currentStep, previous, next, totalSteps, validate } = useStepContext();
    const screenClass = useScreenClass();

    const leftGroup = getLeftGroup(currentStep, previous, PreviousAction);
    const rightButton = getRightButton(async () => {
        const isValid = await validate(currentStep);
        if (currentStep === totalSteps || !isValid) {
            return;
        }

        next();
    }, NextAction);

    const isMobile = screenClass === 'xs';

    if (isMobile) {
        /*
         * On mobile (non-exhaustive list of differences):
         *
         * - Information appears above the buttons.
         * - If there's no buy now action, the right button appears, full-width.
         * - If there is a buy now action, there is a stack of buttons instead.
         */

        return (
            <BottomBarContainer>
                {Information && (
                    <ResponsiveFlexBox
                        justifyContent="flex-start"
                        paddingTop={5}
                        paddingX={5}
                        sm={{ justifyContent: 'center', paddingTop: 4 }}
                    >
                        <Information />
                    </ResponsiveFlexBox>
                )}
                <ResponsiveFlexBox
                    alignItems="center"
                    justifyContent="space-between"
                    gap={3}
                    paddingY={5}
                    paddingX={5}
                    sm={{
                        paddingX: 5,
                        paddingY: 4,
                        style: {
                            height: '96px',
                        },
                    }}
                    md={{
                        paddingX: 8,
                        paddingY: 4,
                    }}
                    style={{ width: '100%' }}
                >
                    {leftGroup}
                    {!BuyNowAction && (
                        <ResponsiveFlexBox
                            alignItems="center"
                            style={{ flex: 1 }}
                            sm={{ style: { flex: undefined } }}
                        >
                            {rightButton}
                        </ResponsiveFlexBox>
                    )}
                </ResponsiveFlexBox>
                {BuyNowAction && (
                    <ResponsiveFlexBox
                        alignItems="center"
                        justifyContent="space-between"
                        paddingBottom={4}
                        paddingX={5}
                        style={{
                            width: '100%',
                        }}
                        sm={{
                            paddingX: 7,
                            style: {
                                height: '96px',
                            },
                        }}
                    >
                        {rightButton}
                        <BuyNowAction />
                    </ResponsiveFlexBox>
                )}
            </BottomBarContainer>
        );
    }

    return (
        <BottomBarContainer>
            <ResponsiveFlexBox
                alignItems="center"
                justifyContent="space-between"
                paddingY={4}
                paddingX={5}
                sm={{
                    paddingX: 5,
                    style: {
                        height: '96px',
                    },
                }}
                md={{
                    paddingX: 8,
                }}
            >
                {leftGroup}
                <FlexBox alignItems="center">
                    {Information && <Information />}
                    {rightButton}
                    {BuyNowAction ? <BuyNowAction /> : null}
                </FlexBox>
            </ResponsiveFlexBox>
        </BottomBarContainer>
    );
};

function getLeftGroup(currentStep: number, onClick: () => void, Component?: React.ComponentType) {
    if (Component != null) {
        return <Component />;
    } else if (currentStep !== 1) {
        return (
            <ResponsiveButton
                skin="secondary"
                onClick={onClick}
                disabled={currentStep === 0}
                iconPosition="left"
                sm={{
                    skin: 'tertiary',
                }}
            >
                <Icon iconType="arrowLeft" />
                {__('Back')}
            </ResponsiveButton>
        );
    }

    return <span />;
}

function getRightButton(onClick: () => void, Component?: React.ComponentType) {
    if (Component != null) {
        return <Component />;
    }

    return (
        <ResponsiveButton
            onClick={onClick}
            skin="primary"
            width="full-width"
            sm={{ width: 'standard' }}
        >
            {__('Next')}
        </ResponsiveButton>
    );
}
